import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, FileText } from "lucide-react";

/**
 * Receta-specific confirmation screen.
 * Reached after submitting a "recetas" category on tenants where Thales does not bill
 * (e.g. Fleni). Intentionally avoids any pricing copy and any SLA copy that belongs to
 * paid consultations. The institutional system (SIDCA-Receta) is the source of truth.
 */
export default function RecetaConfirmada() {
  const { consultId } = useParams();
  const navigate = useNavigate();

  // Reuse artifacts persisted by IntakeForm.js right after the patient submitted.
  // Available across the whole session; enable a discreet "Iniciar otra solicitud"
  // CTA that pre-fills all administrative data via the existing prefill flow.
  let reusePrefillToken = null;
  let lastSourceSlug = null;
  try {
    reusePrefillToken = sessionStorage.getItem('fleni:reuse_prefill_token');
    lastSourceSlug = sessionStorage.getItem('fleni:last_source_slug');
  } catch (e) { /* silent */ }

  const canStartAnother = !!(reusePrefillToken && lastSourceSlug);

  const startAnother = () => {
    // sourceSlug looks like "fleni/varela"; the intake route is /:institutionSlug/:doctorSlug
    navigate(`/${lastSourceSlug}?prefill_token=${encodeURIComponent(reusePrefillToken)}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div
        className="bg-white max-w-xl w-full rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
        data-testid="receta-confirmada-card"
      >
        <div className="bg-emerald-50 px-6 py-8 border-b border-emerald-100 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
          <h1 className="text-2xl font-semibold text-emerald-900">
            Solicitud de receta enviada
          </h1>
          <p className="text-sm text-emerald-700 mt-2">
            Tu médico recibió la solicitud y la procesará en su próximo turno disponible.
          </p>
        </div>

        <div className="p-6 space-y-5 text-slate-700">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-slate-900">¿Qué sigue?</p>
              <p className="text-sm mt-1">
                Cuando tu médico confeccione la receta, la vas a recibir por el canal oficial
                de tu institución. No necesitás hacer nada más por ahora.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-slate-900">¿Necesitás otra renovación?</p>
              <p className="text-sm mt-1">
                Podés iniciar una nueva solicitud cuando lo necesites. Si tu situación clínica
                cambió (dosis, efectos adversos, dudas), usá la categoría{" "}
                <span className="font-medium">Consulta sobre tu tratamiento</span>.
              </p>
            </div>
          </div>

          {consultId && (
            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              ID interno: {consultId}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              data-testid="receta-back-home-btn"
              onClick={() => navigate("/")}
            >
              Volver al inicio
            </Button>
            {canStartAnother && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                data-testid="receta-start-another-btn"
                onClick={startAnother}
              >
                Iniciar otra solicitud
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
